import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NavHashLink } from 'react-router-hash-link';
import { DashboardText } from '../common/Dashboard';
import ProfileComponent from '../common/Profile/ProfileComponent';
import useSidebarData from '../Sidebar/Data';
import useAuth from '../../Hooks/useAuth';
import { pathMatches, sectionActive } from '../Sidebar/businessSidebarNav';
import './header.css';

const SideBarDrawer = ({ toggleDrawer, close }) => {
  const [open, setOpen] = useState(null);
  const { auth } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const sidebarLinks = useSidebarData({ toggleDrawer });

  const toggleHandler = (index) => {
    setOpen((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const expandedIndex = sidebarLinks.findIndex((item) => sectionActive(item, pathname));
    if (expandedIndex >= 0 && sidebarLinks[expandedIndex]?.subMenu) {
      setOpen(expandedIndex);
    }
  }, [pathname]);

  if (!auth?.user) return null;

  return (
    <nav className="flex flex-col items-center justify-center lg:hidden">
      <ProfileComponent />
      <ul className="pt-2 pb-4 space-y-1 w-[200px] mt-5 lg:mt-0 text-offblack">
        <li>
          {sidebarLinks?.map((e, index) => {
            const itemActive = sectionActive(e, pathname);

            if (e.subMenu) {
              const expanded = open === index;
              return (
                <li key={e.linkName || index}>
                  <button
                    type="button"
                    className={
                      expanded || itemActive
                        ? 'flex items-center justify-between p-2 py-3 text-primaryblue w-full'
                        : 'flex items-center justify-between p-2 py-3 hover:text-primaryblue w-full'
                    }
                    aria-expanded={expanded}
                    onClick={() => toggleHandler(index)}
                  >
                    <span className="flex items-center gap-3">
                      {itemActive ? e.iconSelected : e.icon}
                      <DashboardText variant="nav" as="span" className="capitalize">
                        {e.linkName}
                      </DashboardText>
                    </span>
                    <i
                      className="fi fi-rs-angle-down text-sm text-offblack leading-none"
                      aria-hidden
                    />
                  </button>
                  {expanded && (
                    <ul className="transition duration-150 ease-in-out py-2 space-y-2 flex flex-col w-full pl-3">
                      {e.subMenu.map((sub) => (
                        <li key={sub.id}>
                          <NavHashLink
                            to={sub.link}
                            onClick={close}
                            className={`flex items-center gap-3 hover:text-primaryblue w-full px-5 py-2 ${
                              pathMatches(pathname, sub.link) ? 'text-primaryblue' : 'text-offblack'
                            }`}
                          >
                            <DashboardText variant="nav" as="span" className="capitalize">
                              {sub.linkName}
                            </DashboardText>
                            {typeof sub.badge !== 'undefined' && (
                              <span className="inline-flex items-center justify-center rounded-full bg-alertred text-white text-[11px] min-w-5 h-5 px-1 ml-1 leading-none tabular-nums">
                                {sub.badge}
                              </span>
                            )}
                          </NavHashLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            if (e.isLogout) {
              return (
                <button
                  key={e.linkName || index}
                  type="button"
                  onClick={() => {
                    e.onClick?.();
                    close?.();
                  }}
                  className="flex items-center p-2 py-3 gap-3 text-offblack hover:text-alertred w-full transition-colors"
                >
                  <span className="inline-block">{e.icon}</span>
                  <DashboardText variant="nav" as="span" className="capitalize">
                    {e.linkName}
                  </DashboardText>
                </button>
              );
            }

            const linkActive = pathMatches(pathname, e.link);
            return (
              <Link
                key={e.linkName || index}
                to={e.link}
                onClick={() => {
                  setOpen(null);
                  close?.();
                  e.onClick?.();
                }}
                className={`flex items-center p-2 py-3 gap-3 hover:text-primaryblue ${
                  linkActive ? 'text-primaryblue' : 'text-offblack'
                }`}
              >
                <span className="inline-block">{linkActive && e.iconSelected ? e.iconSelected : e.icon}</span>
                <span className="flex items-center gap-2">
                  <DashboardText variant="nav" as="span" className="capitalize">
                    {e.linkName}
                  </DashboardText>
                  {typeof e.badge !== 'undefined' && (
                    <span className="inline-flex items-center justify-center rounded-full bg-alertred text-white text-[11px] min-w-5 h-5 px-1 ml-1 leading-none tabular-nums">
                      {e.badge}
                    </span>
                  )}
                </span>
              </Link>
            );
          })}
        </li>
      </ul>
    </nav>
  );
};

export default SideBarDrawer;
